import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navigation from './Navigation';
import Footer from './Footer';
import './CarDetails.css';
import './App.css';

function CarDetails() {
  const { category, link } = useParams();
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [lookChoice, setLookChoice] = useState('old'); // 'old' or 'new'
  const hasFetched = useRef(false);
  const currentLink = useRef(link);
  const currentCategory = useRef(category);


  // Helper function to format category name for display
  const formatCategoryName = (categorySlug) => {
    return categorySlug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Helper function to format car name for display
  const formatCarName = (carLink) => {
    return carLink
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Helper function to format currency in Indian numbering system
  const formatIndianCurrency = (amount) => {
    // Convert to number if it's a string
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    
    // Return '0' if invalid or zero
    if (!numAmount || isNaN(numAmount)) return '0';
    
    // Format using Indian locale
    return numAmount.toLocaleString('en-IN');
  };

  // Header component (reused from App.js)
  const Header = ({ children }) => (
    <header className="App-header">
      {children}
    </header>
  );

  // Carousel navigation functions
  const nextImage = () => {
    if (car && car.images) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === car.images.length - 1 ? 0 : prevIndex + 1
      );
    }
  };

  const prevImage = () => {
    if (car && car.images) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === 0 ? car.images.length - 1 : prevIndex - 1
      );
    }
  };

  const selectImage = (index) => {
    setCurrentImageIndex(index);
  };

  // Zoom functionality
  const openZoom = () => {
    setIsZoomed(true);
    setZoomPosition({ x: 0, y: 0 });
  };

  const closeZoom = () => {
    setIsZoomed(false);
    setZoomPosition({ x: 0, y: 0 });
    setIsDragging(false);
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - zoomPosition.x,
      y: e.clientY - zoomPosition.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    setZoomPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Navigation handler for Contact Us button
  const handleContactUs = () => {
    navigate('/contact');
  };

  // Rent Car button handler - navigate to booking page with car link
  const handleRentCarClick = () => {
    if (link) {
      navigate('/booking', {
        state: {
          carLink: link
        }
      });
    }
  };

  // Handle escape key to close zoom
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isZoomed) {
        closeZoom();
      }
    };

    if (isZoomed) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isZoomed]);

  // Update document title when car data loads and track product view
  useEffect(() => {
    if (car && car.title) {
      document.title = car.title;
    }
    
    // Track Google Analytics 4 product view event
    if (car && typeof window.gtag === 'function') {
      // Get the current pricing based on look choice
      const currentPrice4hr = lookChoice === 'old' ? car.price4hrOld : car.price4hr;
      const currentPrice8hr = lookChoice === 'old' ? car.price8hrOld : car.price8hr;
      
      // Use the lower price if both exist, or the only available price
      let displayPrice = 0;
      if (currentPrice4hr > 0 && currentPrice8hr > 0) {
        displayPrice = Math.min(currentPrice4hr, currentPrice8hr);
      } else if (currentPrice4hr > 0) {
        displayPrice = currentPrice4hr;
      } else if (currentPrice8hr > 0) {
        displayPrice = currentPrice8hr;
      }
      
      window.gtag('event', 'view_item', {
        currency: 'INR',
        value: displayPrice,
        items: [{
          item_id: link,
          item_name: `${car.make} ${car.model}`,
          item_category: car.category,
          item_brand: car.make,
          price: displayPrice,
          quantity: 1
        }]
      });
    }
    
    // Update meta description tag if seoDescription is available
    let metaDescriptionTag = document.querySelector('meta[name="description"]');
    
    if (car && car.seoDescription) {
      if (!metaDescriptionTag) {
        // Create meta description tag if it doesn't exist
        metaDescriptionTag = document.createElement('meta');
        metaDescriptionTag.name = 'description';
        document.head.appendChild(metaDescriptionTag);
      }
      metaDescriptionTag.content = car.seoDescription;
    } else if (metaDescriptionTag && car && !car.seoDescription) {
      // Remove meta description tag if seoDescription is not available
      metaDescriptionTag.remove();
    }
    
    // Update meta keywords tag if seoTags is available
    let metaKeywordsTag = document.querySelector('meta[name="keywords"]');
    
    if (car && car.seoTags) {
      if (!metaKeywordsTag) {
        // Create meta keywords tag if it doesn't exist
        metaKeywordsTag = document.createElement('meta');
        metaKeywordsTag.name = 'keywords';
        document.head.appendChild(metaKeywordsTag);
      }
      metaKeywordsTag.content = car.seoTags;
    } else if (metaKeywordsTag && car && !car.seoTags) {
      // Remove meta keywords tag if seoTags is not available
      metaKeywordsTag.remove();
    }
    
    return () => {
      // Reset to default title on unmount
      document.title = 'Vidaai - Wedding Car Rentals';
      // Clean up meta description tag on unmount
      const metaDescTag = document.querySelector('meta[name="description"]');
      if (metaDescTag) {
        metaDescTag.remove();
      }
      // Clean up meta keywords tag on unmount
      const metaKeyTag = document.querySelector('meta[name="keywords"]');
      if (metaKeyTag) {
        metaKeyTag.remove();
      }
    };
  }, [car, link, lookChoice]);

  useEffect(() => {
    // Reset fetch flag if category or link changes
    if (currentCategory.current !== category || currentLink.current !== link) {
      hasFetched.current = false;
      currentCategory.current = category;
      currentLink.current = link;
    }

    // Prevent duplicate fetch in strict mode
    if (hasFetched.current) return;
    if (!category || !link) return;
    
    hasFetched.current = true;

    const fetchCarDetails = async () => {
      try {
        setLoading(true);
        const response = await fetch(`https://api.vidaai.com/api/v1/car/${link}`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Check if the API response has the expected format
        if (data.success && data.data) {
          const carData = data.data;
          
          // Parse features from API response or use defaults
          let features = ['Air Conditioning', 'Free Decoration', 'Chauffeur']; // Default features
          if (carData.features && carData.features.trim()) {
            features = carData.features.split(',').map(feature => feature.trim());
          }
          
          // Parse images from API response or use defaults
          // Images are now image IDs for Cloudflare CDN
          let images = ['/logo.png']; // Default image
          if (carData.images && carData.images.trim()) {
            // Split comma-separated image names/IDs and convert to Cloudflare URLs
            const imageIds = carData.images.split(',').map(img => img.trim()).filter(Boolean);
            images = imageIds.map(imageId => {
              // Use Cloudflare CDN URL with height parameter (fixed at 400px like other pages)
              return `https://vidaai.com/cdn-cgi/imagedelivery/Hr3zVE-rhnx6pqKYXYRDzw/${imageId}/h=400`;
            });
          } else if (carData.image && carData.image.trim()) {
            // Use the primary image ID from API with Cloudflare CDN
            images = [`https://vidaai.com/cdn-cgi/imagedelivery/Hr3zVE-rhnx6pqKYXYRDzw/${carData.image}/h=400`];
          }
          
          // Transform API data to match our component's expected structure
          const transformedCar = {
            make: carData.make,
            model: carData.model,
            category: carData.category || category, // Store category from API or URL param
            year: 2023, // Default since not in API
            color: 'Silver', // Default since not in API
            price: carData.price,
            price4hr: carData['4-hr'] || 0,
            price8hr: carData['8-hr'] || 0,
            price4hrOld: carData['4-hr-old'] || 0,
            price8hrOld: carData['8-hr-old'] || 0,
            extraHourOld: carData['extra-hr-old'] || carData['extra-hr'] || 0,
            extraKmOld: carData['extra-km-old'] || carData['extra-km'] || 0,
            mileage: 15000, // Default since not in API
            engine: '2.0L Turbo', // Default since not in API
            transmission: 'Automatic', // Default since not in API
            fuelType: carData['fuel-type'] || 'Fuel', // Default since not in API
            doors: carData['doors'] || 4,
            seats: carData['seats'] || 4,
            luggage: carData['luggage'] || 2,
            extraHour: carData['extra-hr'] || 0,
            extraKm: carData['extra-km'] || 0,
            deposit: carData['deposit'] || 'TBD',
            decorationPrice: carData['decoration-price'] || 0,
            roadLegal: carData['road-legal'] || 'Yes', // Default to 'Yes' if not specified
            title: carData.title || 'Decorated Wedding Car Rentals | Chauffeur Driven | Vidaai',
            seoDescription: carData['seo-description'] || null,
            seoTags: carData['seo-tags'] || null,
            hr4Reservation: carData['hr4-reservation'] || {"deposit": 5000, "remaining": 6000, "total": 11000, "gst": 1980, "grandTotal": 12980},
            hr8Reservation: carData['hr8-reservation'] || {"deposit": 5000, "remaining": 10000, "total": 15000, "gst": 2700, "grandTotal": 17700},
            features: features,
            description: carData.description && carData.description.trim() 
              ? carData.description 
              : `This ${carData.make} ${carData.model} is perfect for your special day. Combining luxury, comfort, and style, it offers an unforgettable experience for weddings and special occasions.`,
            images: images
          };
          setCar(transformedCar);
          setCurrentImageIndex(0); // Reset image index when car data loads
          
          // Set default lookChoice to 'new' if category is vintage OR if no old pricing is available
          const isVintage = (carData.category || category)?.toLowerCase() === 'vintage';
          const hasOldPricing = (carData['4-hr-old'] > 0 || carData['8-hr-old'] > 0);
          
          if (isVintage || !hasOldPricing) {
            setLookChoice('new');
          }
        } else if (data.success === false) {
          throw new Error('Car not found');
        } else {
          throw new Error('Invalid API response format');
        }
      } catch (err) {
        setError(err.message);
        console.error('Error fetching car details:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCarDetails();
  }, [category, link]);

  if (loading) {
    const categoryName = formatCategoryName(category);
    const carName = formatCarName(link);
    
    return (
      <div className="App">
        <Navigation />
        <div className="top-breadcrumb-container">
          <nav className="breadcrumb-nav">
            <Link to="/" className="breadcrumb-item">
              <span>Home</span>
            </Link>
            <span className="breadcrumb-separator">›</span>
            <Link to={`/listing/${category}`} className="breadcrumb-item">
              <span>{categoryName}</span>
            </Link>
            <span className="breadcrumb-separator">›</span>
            <span className="breadcrumb-item current">
              {carName}
            </span>
          </nav>
        </div>
        <Header>
          <div className="loading">Loading car details...</div>
        </Header>
      </div>
    );
  }

  if (error) {
    const categoryName = formatCategoryName(category);
    const carName = formatCarName(link);
    
    return (
      <div className="App">
        <Navigation />
        <div className="top-breadcrumb-container">
          <nav className="breadcrumb-nav">
            <Link to="/" className="breadcrumb-item">
              <span>Home</span>
            </Link>
            <span className="breadcrumb-separator">›</span>
            <Link to={`/listing/${category}`} className="breadcrumb-item">
              <span>{categoryName}</span>
            </Link>
            <span className="breadcrumb-separator">›</span>
            <span className="breadcrumb-item current">
              {carName}
            </span>
          </nav>
        </div>
        <Header>
          <div className="error">
            <p>Error loading car details: {error}</p>
          </div>
        </Header>
      </div>
    );
  }

  if (!car) {
    const categoryName = formatCategoryName(category);
    const carName = formatCarName(link);
    
    return (
      <div className="App">
        <Navigation />
        <div className="top-breadcrumb-container">
          <nav className="breadcrumb-nav">
            <Link to="/" className="breadcrumb-item">
              <span>Home</span>
            </Link>
            <span className="breadcrumb-separator">›</span>
            <Link to={`/listing/${category}`} className="breadcrumb-item">
              <span>{categoryName}</span>
            </Link>
            <span className="breadcrumb-separator">›</span>
            <span className="breadcrumb-item current">
              {carName}
            </span>
          </nav>
        </div>
        <Header>
          <div className="error">
            <p>Car not found</p>
          </div>
        </Header>
      </div>
    );
  }

  const categoryName = formatCategoryName(category);
  const carName = formatCarName(link);

  return (
    <div className="App">
      <Navigation />
      <div className="top-breadcrumb-container">
        <nav className="breadcrumb-nav">
          <Link to="/" className="breadcrumb-item">
            <span>Home</span>
          </Link>
          <span className="breadcrumb-separator">›</span>
          <Link to={`/listing/${category}`} className="breadcrumb-item">
            <span>{categoryName}</span>
          </Link>
          <span className="breadcrumb-separator">›</span>
          <span className="breadcrumb-item current">
            {car.make} {car.model}
          </span>
        </nav>
      </div>
      <Header>
        <div className="car-details-container">
          <h2>{car.make} {car.model}</h2>
          <div className="car-details-content">
            <div className="car-images">
              <div className="image-carousel">
                {car.images.length > 1 && (
                  <button className="carousel-nav prev" onClick={prevImage}>
                    ‹
                  </button>
                )}
                <img 
                  src={car.images[currentImageIndex]} 
                  alt={`${car.make} ${car.model} - Image ${currentImageIndex + 1}`} 
                  className="car-main-image"
                  onClick={openZoom}
                  style={{ cursor: 'zoom-in' }}
                />
                {car.images.length > 1 && (
                  <button className="carousel-nav next" onClick={nextImage}>
                    ›
                  </button>
                )}
              </div>
              
              {car.images.length > 1 && (
                <div className="image-thumbnails">
                  {car.images.map((image, index) => (
                    <img
                      key={index}
                      src={image}
                      alt={`${car.make} ${car.model} thumbnail ${index + 1}`}
                      className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                      onClick={() => selectImage(index)}
                    />
                  ))}
                </div>
              )}
            </div>
            
            <div className="car-info">
              <div className="car-specs">
                <div className="spec-group">
                  <h3>Pricing</h3>
                  
                  {car.category?.toLowerCase() !== 'vintage' && (car.price4hrOld > 0 || car.price8hrOld > 0) && (
                    <div className="look-choice-section">
                      <h4>Select Car Age / Design</h4>
                      <div className="look-choice-toggle">
                        <button
                          type="button"
                          className={`look-choice-button ${lookChoice === 'old' ? 'active' : ''}`}
                          onClick={() => setLookChoice('old')}
                        >
                          <div className="choice-title">Old Look</div>
                          <div className="choice-subtitle">Model year 2019 or before</div>
                        </button>
                        <button
                          type="button"
                          className={`look-choice-button ${lookChoice === 'new' ? 'active' : ''}`}
                          onClick={() => setLookChoice('new')}
                        >
                          <div className="choice-title">New Look</div>
                          <div className="choice-subtitle">Model year 2020 or later</div>
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="pricing-table">
                    {((lookChoice === 'old' ? car.price4hrOld : car.price4hr) > 0) && (
                      <div className="pricing-row">
                        <div className="pricing-item">
                          <strong>{car.roadLegal === 'Yes' ? '4 Hours / 40 KMs:' : '4 Hours:'}</strong>
                          <span className="price-value">₹{formatIndianCurrency(lookChoice === 'old' ? car.price4hrOld : car.price4hr)}</span>
                        </div>
                      </div>
                    )}
                    {((lookChoice === 'old' ? car.price8hrOld : car.price8hr) > 0) && (
                      <div className="pricing-row">
                        <div className="pricing-item">
                          <strong>{car.roadLegal === 'Yes' ? '8 Hours / 80 KMs:' : '8 Hours:'}</strong>
                          <span className="price-value">₹{formatIndianCurrency(lookChoice === 'old' ? car.price8hrOld : car.price8hr)}</span>
                        </div>
                      </div>
                    )}
                    {((lookChoice === 'old' ? car.extraHourOld : car.extraHour) > 0) && (
                      <div className="pricing-row">
                        <div className="pricing-item">
                          <strong>Extra Hours:</strong>
                          <span className="price-value">₹{formatIndianCurrency(lookChoice === 'old' ? car.extraHourOld : car.extraHour)}/hr</span>
                        </div>
                      </div>
                    )}
                    {((lookChoice === 'old' ? car.extraKmOld : car.extraKm) > 0) && (
                      <div className="pricing-row">
                        <div className="pricing-item">
                          <strong>Extra Kms:</strong>
                          <span className="price-value">₹{formatIndianCurrency(lookChoice === 'old' ? car.extraKmOld : car.extraKm)}/km</span>
                        </div>
                      </div>
                    )}
                    <div className="pricing-row">
                      <div className="pricing-item">
                        <strong>Decoration:</strong>
                        <span className="price-value">{car.decorationPrice > 0 ? `₹${formatIndianCurrency(car.decorationPrice)}` : 'FREE'}</span>
                      </div>
                    </div>
                    {(car.deposit && car.deposit !== 'TBD' && car.deposit > 0) && (
                      <div className="pricing-row">
                        <div className="pricing-item">
                          <strong>Deposit to Reserve:</strong>
                          <span className="price-value">₹{formatIndianCurrency(car.deposit)}</span>
                        </div>
                      </div>
                    )}
                    <div className="pricing-note">
                      <p><em>* Pay deposit to reserve the car. Balance amount must be paid 7 days prior. Taxes extra.</em></p>
                    </div>
                  </div>
                </div>

                <div className="spec-group">
                  <h3>Vehicle Information</h3>
                  <div className="vehicle-info-grid">
                    <div className="vehicle-info-item">
                      <img src="/icons/seat.png" alt="Seating" className="vehicle-icon" />
                      <div className="vehicle-info-text">
                        <strong>Seating</strong>
                        <span>{car.seats}</span>
                      </div>
                    </div>
                    <div className="vehicle-info-item">
                      <img src="/icons/bag.png" alt="Luggage" className="vehicle-icon" />
                      <div className="vehicle-info-text">
                        <strong>Luggage</strong>
                        <span>{car.luggage}</span>
                      </div>
                    </div>
                    <div className="vehicle-info-item">
                      <img src="/icons/fuel.png" alt="Fuel" className="vehicle-icon" />
                      <div className="vehicle-info-text">
                        <strong>Fuel Type</strong>
                        <span>{car.fuelType}</span>
                      </div>
                    </div>
                    <div className="vehicle-info-item">
                      <img src="/icons/door.png" alt="Doors" className="vehicle-icon" />
                      <div className="vehicle-info-text">
                        <strong>Doors</strong>
                        <span>{car.doors} doors</span>
                      </div>
                    </div>
                    <div className="vehicle-info-item">
                      <img src="/icons/color.png" alt="Colour" className="vehicle-icon" />
                      <div className="vehicle-info-text">
                        <strong>Colour</strong>
                        <span>Based on availability</span>
                      </div>
                    </div>
                    <div className="vehicle-info-item">
                      <img src="/icons/chauffeur.png" alt="Driver" className="vehicle-icon" />
                      <div className="vehicle-info-text">
                        <strong>Driver</strong>
                        <span>Chauffeur driven</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="spec-group">
                  <h3>Description</h3>
                  <p className="car-description">{car.description}</p>
                </div>

                <div className="spec-group">
                  <h3>Features</h3>
                  <div className="features-list">
                    {car.features?.map((feature, index) => (
                      <span key={index} className="feature-tag">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="action-buttons">
                  <button className="rent-button" onClick={handleRentCarClick}>Book Car</button>
                  <button className="contact-button" onClick={handleContactUs}>Contact Us</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Header>
      
      {/* Zoom Modal */}
      {isZoomed && (
        <div 
          className="zoom-overlay"
          onClick={closeZoom}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <div className="zoom-controls">
            <button className="zoom-close" onClick={closeZoom}>×</button>
            <span className="zoom-instructions">Click and drag to pan • Press ESC or click outside to close</span>
          </div>
          <img
            src={car.images[currentImageIndex]}
            alt={`${car.make} ${car.model} - Zoomed Image ${currentImageIndex + 1}`}
            className="zoom-image"
            style={{
              transform: `translate(${zoomPosition.x}px, ${zoomPosition.y}px)`,
              cursor: isDragging ? 'grabbing' : 'grab'
            }}
            onMouseDown={handleMouseDown}
            onClick={(e) => e.stopPropagation()}
            draggable={false}
          />
        </div>
      )}

      <Footer />
    </div>
  );
}

export default CarDetails;
